import React from 'react';
import Link from 'next/link';
import { Heart, Plus } from 'lucide-react';
import { ProductBase } from '@/types/product';
import { CartItem } from '@/types/cart';
import { BreadcrumbMenu } from '@/types/menu';
import { useProductTracking } from '@/hooks/useProductTracking';
import { useAddToCart } from '@/hooks/useAddToCart';
import StockBadge from '@/components/common/StockBadge';
interface ProductCardProps {
  index?: number;
  breadcrumbMenu: BreadcrumbMenu;
  product: ProductBase;
  backgroundColor?: string;
  onFavoriteToggle?: (id: string) => void;
  className?: string;
  viewMode?: 'grid' | 'list';
  /** Evita H3 en títulos de producto (p. ej. carruseles de la ficha) */
  productTitleAsParagraph?: boolean;
}

// Función para convertir hex a rgba
function hexToRgba(hex: string, alpha: number): string {
  // Validar que hex sea un string válido
  if (!hex || typeof hex !== 'string') {
    console.error('Invalid hex color:', hex);
    return 'rgba(255, 255, 255, 1)'; // Fallback a blanco
  }

  // Elimina el # si está presente
  hex = hex.replace('#', '');

  // Validar que sea un hex válido (6 caracteres)
  if (hex.length !== 6) {
    console.error('Invalid hex length:', hex);
    return 'rgba(255, 255, 255, 1)'; // Fallback a blanco
  }

  // Convierte a valores RGB
  const bigint = parseInt(hex, 16);

  // Validar que la conversión sea exitosa
  if (isNaN(bigint)) {
    console.error('Invalid hex value:', hex);
    return 'rgba(255, 255, 255, 1)'; // Fallback a blanco
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  // Validar que alpha esté entre 0 y 1
  const validAlpha = Math.max(0, Math.min(1, alpha));

  return `rgba(${r}, ${g}, ${b}, ${validAlpha})`;
}

// Función para formatear precios con coma
function formatPrice(price: number): string {
  return price.toFixed(2).replace('.', ',');
}

const ProductCard: React.FC<ProductCardProps> = ({
  index,
  breadcrumbMenu,
  product,
  backgroundColor,
  onFavoriteToggle,
  className = '',
  viewMode = 'grid',
  productTitleAsParagraph = false,
}) => {
  const ProductTitleTag = productTitleAsParagraph ? 'p' : 'h3';
  const backgroundStyle = {
    backgroundColor: hexToRgba(backgroundColor || '#FFDE3B', 0.45),
  };
  const { trackProductSelection } = useProductTracking();

  const currentPriceNumber = parseFloat(String(product?.priceFinalInc));
  const originalPriceNumber =
    product?.priceOriginalInc !== undefined
      ? parseFloat(String(product?.priceOriginalInc))
      : undefined;
  const hasDiscount =
    originalPriceNumber !== undefined &&
    !isNaN(originalPriceNumber) &&
    originalPriceNumber > currentPriceNumber;
  const discountPercent = hasDiscount
    ? Math.round(((originalPriceNumber - currentPriceNumber) / originalPriceNumber) * 100)
    : 0;

  const handleFavoriteToggle = () => {
    if (onFavoriteToggle) {
      onFavoriteToggle(product?.id.toString() || '');
    }
  };

  const { addToCart } = useAddToCart();

  const handleOnClick = () => {
    const itemListId =
      breadcrumbMenu.menuLevel3?.slug ??
      breadcrumbMenu.menuLevel2?.slug ??
      breadcrumbMenu.menuLevel1.slug;
    const itemListName =
      breadcrumbMenu.menuLevel3?.name ??
      breadcrumbMenu.menuLevel2?.name ??
      breadcrumbMenu.menuLevel1.name;
    trackProductSelection(
      {
        index: index || 0,
        item_id: product.id.toString(),
        item_name: product.title,
        item_category: breadcrumbMenu.menuLevel1.name,
        item_category2: breadcrumbMenu.menuLevel2?.name,
        item_category3: breadcrumbMenu.menuLevel3?.name,
        item_brand: product.brand,
        item_list_id: itemListId,
        item_list_name: itemListName,
        price: product.priceFinalInc,
        discount: product.discountInc,
        bar_code: product.barcode,
      },
      itemListId,
      itemListName
    );
  };

  const handleAddToCart = async () => {
    if (product) {
      // Crear un CartItem desde el ProductBase
      const cartItem: CartItem = {
        id: product.id.toString(),
        name: product.title,
        image: product.image,
        price: product.priceFinalInc,
        tax: product.priceFinalInc - product.priceFinalExc,
        quantity: 1,
        weight: product.weight || '',
        brand: product.brand || '',
        barcode: product.barcode || '',
        category: product.breadcrumb?.[0]?.name || '',
        category2: product.breadcrumb?.[1]?.name || '',
        category3: product.breadcrumb?.[2]?.name || '',
        variant: 'default',
        format: 'default',
      };

      // Agregar al carrito (el toast se mostrará automáticamente desde el store)
      addToCart(cartItem);
    }
  };

  const isListView = viewMode === 'list';

  const mainImage = product.images?.find((p) => p.isMain) ?? product.images?.[0];

  return (
    <div data-component-id="src/components/ui/ProductCard.tsx:159:4" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="159" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${isListView ? 'w-full h-auto' : 'w-[267px] h-[389px]'} ${className}`}>
      <div data-component-id="src/components/ui/ProductCard.tsx:160:6" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="160" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`relative overflow-hidden ${isListView ? 'w-full h-auto min-h-[120px] flex flex-row items-center gap-4 p-4' : 'w-[267px] h-[389px]'} rounded-[17px] border-0 shadow-none transition-all duration-300 hover:shadow-lg hover:scale-105`}
        style={backgroundStyle}
      >
        {isListView ? (
          // Vista de lista
          <>
            {/* Product Image */}
            <div data-component-id="src/components/ui/ProductCard.tsx:168:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="168" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-24%20h-24%20relative%20overflow-hidden%22%7D" className="flex-shrink-0 w-24 h-24 relative overflow-hidden">
              {/* Discount Badge para vista list */}
              {hasDiscount && (
                <div data-component-id="src/components/ui/ProductCard.tsx:171:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="171" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-1%20left-1%20z-10%20bg-%5B%23fcc00a%5D%20px-2%20py-0.5%20rounded-full%20shadow-md%22%7D" className="absolute top-1 left-1 z-10 bg-[#fcc00a] px-2 py-0.5 rounded-full shadow-md">
                  <span data-component-id="src/components/ui/ProductCard.tsx:172:18" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="172" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-%5B11px%5D%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                    className="text-[#3d1c1a] text-[11px] font-bold"
                    style={{
                      fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                      fontWeight: '700',
                    }}
                  >
                    -{discountPercent}%
                  </span>
                </div>
              )}
              <Link data-component-id="src/components/ui/ProductCard.tsx:183:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="183" data-component-file="ProductCard.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D" href={product.url || ''} onClick={handleOnClick}>
                <img data-component-id="src/components/ui/ProductCard.tsx:184:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="184" data-component-file="ProductCard.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%20cursor-pointer%22%7D"
                  src={mainImage?.src || ''}
                  alt={mainImage?.alt || ''}
                  width={80}
                  height={80}
                  className="w-full h-full object-contain cursor-pointer"
                />
              </Link>
            </div>

            {/* Product Info */}
            <div data-component-id="src/components/ui/ProductCard.tsx:195:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="195" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-grow%20text-%5B%233d1c1a%5D%20relative%22%7D" className="flex-grow text-[#3d1c1a] relative">
              {product?.isGlutenFree && (
                <div data-component-id="src/components/ui/ProductCard.tsx:197:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="197" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-0%20right-0%22%7D" className="absolute top-0 right-0">
                  <img data-component-id="src/components/ui/ProductCard.tsx:198:18" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="198" data-component-file="ProductCard.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_25x25.svg%22%2C%22alt%22%3A%22Sin%20Gluten%22%7D"
                    src="/images/img_vector_gray_900_25x25.svg"
                    alt="Sin Gluten"
                    width={20}
                    height={20}
                  />
                </div>
              )}
              <Link data-component-id="src/components/ui/ProductCard.tsx:206:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="206" data-component-file="ProductCard.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D" href={product.url || ''} onClick={handleOnClick}>
                <ProductTitleTag data-component-id="src/components/ui/ProductCard.tsx:207:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="207" data-component-file="ProductCard.tsx" data-component-name="ProductTitleTag" data-component-content="%7B%22elementName%22%3A%22ProductTitleTag%22%2C%22className%22%3A%22text-lg%20leading-6%20mb-1%20line-clamp-2%20cursor-pointer%20relative%20group%20break-words%20overflow-wrap-anywhere%22%7D"
                  className="text-lg leading-6 mb-1 line-clamp-2 cursor-pointer relative group break-words overflow-wrap-anywhere"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                    fontWeight: '700',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {product?.title}
                  <span data-component-id="src/components/ui/ProductCard.tsx:217:18" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="217" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20bottom-0%20left-0%20w-0%20h-0.5%20bg-%5B%233d1c1a%5D%20transition-all%20duration-300%20group-hover%3Aw-full%22%7D" className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#3d1c1a] transition-all duration-300 group-hover:w-full"></span>
                </ProductTitleTag>
              </Link>
              <p data-component-id="src/components/ui/ProductCard.tsx:220:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="220" data-component-file="ProductCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20leading-5%20mb-1%22%7D"
                className="text-base leading-5 mb-1"
                style={{
                  fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                  fontWeight: '400',
                }}
              >
                {product.brand}
              </p>
              {product.productCombinations && product.productCombinations.length > 0 && (
                <p data-component-id="src/components/ui/ProductCard.tsx:230:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="230" data-component-file="ProductCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20leading-5%20text-gray-500%22%7D"
                  className="text-base leading-5 text-gray-500"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Regular', Helvetica",
                    fontWeight: '400',
                  }}
                >
                  {product.productCombinations
                    .find((combination) => combination.isDefault)
                    ?.productOptions?.map((option) => option.name)
                    .join(', ')}
                </p>
              )}
            </div>

            {/* Price and Actions */}
            <div data-component-id="src/components/ui/ProductCard.tsx:246:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="246" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-end%20justify-center%20gap-2%20flex-shrink-0%22%7D" className="flex flex-col items-end justify-center gap-2 flex-shrink-0">
              <div data-component-id="src/components/ui/ProductCard.tsx:247:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="247" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-2xl%20leading-7%20text-%5B%233d1c1a%5D%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                className="text-2xl leading-7 text-[#3d1c1a]"
                style={{
                  fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                  fontWeight: '700',
                }}
              >
                {formatPrice(currentPriceNumber)} €/ud.
              </div>
              {hasDiscount && (
                <div data-component-id="src/components/ui/ProductCard.tsx:257:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="257" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                  <span data-component-id="src/components/ui/ProductCard.tsx:258:18" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="258" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20leading-5%20text-%5B%233d1c1a%5D%2F60%20line-through%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                    className="text-sm leading-5 text-[#3d1c1a]/60 line-through"
                    style={{ fontFamily: "'TT_Norms_Pro-Regular', Helvetica", fontWeight: 400 }}
                  >
                    {originalPriceNumber ? formatPrice(originalPriceNumber) : ''} €/ud.
                  </span>
                </div>
              )}
              <div data-component-id="src/components/ui/ProductCard.tsx:266:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="266" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                {/* TODO: Añadir el icono de favorito
                <button
                  onClick={handleFavoriteToggle}
                  className="w-7 h-6 p-0 hover:bg-transparent transition-all duration-300 hover:scale-110 cursor-pointer"
                >
                  <Heart
                    className={`w-7 h-6 ${product?.isFavorite ? 'text-red-500 fill-red-500' : 'text-[#3d1c1a]'}`}
                  />
                </button>
                */}
                <button data-component-id="src/components/ui/ProductCard.tsx:277:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="277" data-component-file="ProductCard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-11%20h-11%20bg-%5B%23fcc00a%5D%20rounded-%5B10px%5D%20p-0%20hover%3Abg-%5B%23fcc00a%5D%2F90%20flex%20items-center%20justify-center%20transition-all%20duration-300%20hover%3Ascale-110%20cursor-pointer%22%7D"
                  onClick={handleAddToCart}
                  className="w-11 h-11 bg-[#fcc00a] rounded-[10px] p-0 hover:bg-[#fcc00a]/90 flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
                >
                  <Plus data-component-id="src/components/ui/ProductCard.tsx:281:18" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="281" data-component-file="ProductCard.tsx" data-component-name="Plus" data-component-content="%7B%22elementName%22%3A%22Plus%22%2C%22className%22%3A%22w-6%20h-6%20text-%5B%233d1c1a%5D%22%7D" className="w-6 h-6 text-[#3d1c1a]" />
                </button>
              </div>
            </div>
          </>
        ) : (
          // Vista de grid (original)
          <div data-component-id="src/components/ui/ProductCard.tsx:288:10" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="288" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20h-full%20p-0%22%7D" className="relative w-full h-full p-0">
            {/* Discount Badge */}
            {hasDiscount && (
              <div data-component-id="src/components/ui/ProductCard.tsx:291:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="291" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B35px%5D%20right-%5B35px%5D%20z-10%20bg-%5B%23fcc00a%5D%20px-3%20py-1%20rounded-full%20shadow-md%22%7D" className="absolute top-[35px] right-[35px] z-10 bg-[#fcc00a] px-3 py-1 rounded-full shadow-md">
                <span data-component-id="src/components/ui/ProductCard.tsx:292:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="292" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-sm%20font-bold%22%2C%22textContent%22%3A%22-%20%25%22%7D"
                  className="text-[#3d1c1a] text-sm font-bold"
                  style={{
                    fontFamily: "'TT_Norms_Pro-Bold', Helvetica",
                    fontWeight: '700',
                  }}
                >
                  -{discountPercent}%
                </span>
              </div>
            )}
            <StockBadge data-component-id="src/components/ui/ProductCard.tsx:303:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="303" data-component-file="ProductCard.tsx" data-component-name="StockBadge" data-component-content="%7B%22elementName%22%3A%22StockBadge%22%2C%22className%22%3A%22top-0%20left-0%22%7D" productId={product.id} className="top-0 left-0" />

            {/* No Gluten Icon */}
            {product?.isGlutenFree && (
              <button data-component-id="src/components/ui/ProductCard.tsx:307:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="307" data-component-file="ProductCard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                className={`absolute w-[25px] h-[25px] p-0 hover:bg-transparent transition-opacity duration-300 hover:opacity-80 cursor-pointer ${hasDiscount ? 'top-[35px] left-[35px]' : 'top-[33px] left-[19px]'}`}
              >
                <img data-component-id="src/components/ui/ProductCard.tsx:310:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="310" data-component-file="ProductCard.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%2Fimages%2Fimg_vector_gray_900_25x25.svg%22%2C%22alt%22%3A%22Sin%20Gluten%22%7D"
                  src="/images/img_vector_gray_900_25x25.svg"
                  alt="Sin Gluten"
                  width={25}
                  height={25}
                />
              </button>
            )}

            {/* Favorite Icon */}
            {/* TODO: Añadir el icono de favorito
            <button
              onClick={handleFavoriteToggle}
              className="absolute top-[34px] left-[214px] w-7 h-6 p-0 hover:bg-transparent transition-all duration-300 hover:scale-110 cursor-pointer"
            >
              
              <Heart
                className={`w-7 h-6 ${product?.isFavorite ? 'text-red-500 fill-red-500' : 'text-[#3d1c1a]'}`}
              />
            </button>
            */}

            {/* Product Image */}
            <Link data-component-id="src/components/ui/ProductCard.tsx:333:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="333" data-component-file="ProductCard.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D" href={product.url || ''} onClick={handleOnClick}>
              <div data-component-id="src/components/ui/ProductCard.tsx:334:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="334" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-x-0%20top-0%20h-%5B240px%5D%20px-5%20pt-4%20pb-2%22%7D" className="absolute inset-x-0 top-0 h-[240px] px-5 pt-4 pb-2">
                <img data-component-id="src/components/ui/ProductCard.tsx:335:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="335" data-component-file="ProductCard.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%20transition-transform%20duration-300%20hover%3Ascale-105%20cursor-pointer%22%7D"
                  src={mainImage?.src || ''}
                  alt={mainImage?.alt || ''}
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105 cursor-pointer"
                />
              </div>
            </Link>

            {/* Product Info */}
            <div data-component-id="src/components/ui/ProductCard.tsx:344:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="344" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B253px%5D%20left-%5B21px%5D%20right-%5B21px%5D%20text-%5B%233d1c1a%5D%20max-h-%5B80px%5D%20overflow-hidden%22%7D" className="absolute top-[253px] left-[21px] right-[21px] text-[#3d1c1a] max-h-[80px] overflow-hidden">
              <Link data-component-id="src/components/ui/ProductCard.tsx:345:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="345" data-component-file="ProductCard.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D" href={product.url || ''} onClick={handleOnClick}>
                <ProductTitleTag data-component-id="src/components/ui/ProductCard.tsx:346:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="346" data-component-file="ProductCard.tsx" data-component-name="ProductTitleTag" data-component-content="%7B%22elementName%22%3A%22ProductTitleTag%22%2C%22className%22%3A%22relative%20group%20text-base%20leading-%5B21px%5D%20font-bold%20line-clamp-2%22%7D"
                  className="relative group text-base leading-[21px] font-bold line-clamp-2"
                  style={{ fontFamily: "'TT_Norms_Pro-Bold', Helvetica" }}
                  title={product.title}
                >
                  {product.title}
                </ProductTitleTag>
              </Link>

              <p data-component-id="src/components/ui/ProductCard.tsx:355:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="355" data-component-file="ProductCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-base%20leading-%5B21px%5D%20truncate%22%7D"
                className="text-base leading-[21px] truncate"
                style={{ fontFamily: "'TT_Norms_Pro-Regular', Helvetica" }}
              >
                {product.brand}
              </p>

              {product.productCombinations && product.productCombinations.length > 0 && (
                <p data-component-id="src/components/ui/ProductCard.tsx:363:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="363" data-component-file="ProductCard.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-%5B%233d1c1a%5D%2F70%20leading-5%20truncate%22%7D" className="text-sm text-[#3d1c1a]/70 leading-5 truncate">
                  {product.productCombinations
                    .find((combination) => combination.isDefault)
                    ?.productOptions?.map((option) => option.name)
                    .join(' / ')}
                </p>
              )}
            </div>

            {/* Price */}
            <div data-component-id="src/components/ui/ProductCard.tsx:373:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="373" data-component-file="ProductCard.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B341px%5D%20left-%5B21px%5D%20flex%20items-center%20gap-3%22%7D" className="absolute top-[341px] left-[21px] flex items-center gap-3">
              <span data-component-id="src/components/ui/ProductCard.tsx:374:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="374" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B%233d1c1a%5D%20text-xl%20tracking-%5B0%5D%20leading-%5B21px%5D%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                className="text-[#3d1c1a] text-xl tracking-[0] leading-[21px] whitespace-nowrap"
                style={{ fontFamily: "'TT_Norms_Pro-Bold', Helvetica", fontWeight: 700 }}
              >
                {formatPrice(currentPriceNumber)} €/ud.
              </span>
              {hasDiscount && (
                <span data-component-id="src/components/ui/ProductCard.tsx:381:16" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="381" data-component-file="ProductCard.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20leading-5%20text-%5B%233d1c1a%5D%2F60%20line-through%20whitespace-nowrap%22%2C%22textContent%22%3A%22%E2%82%AC%2Fud.%22%7D"
                  className="text-sm leading-5 text-[#3d1c1a]/60 line-through whitespace-nowrap"
                  style={{ fontFamily: "'TT_Norms_Pro-Regular', Helvetica", fontWeight: 400 }}
                >
                  {originalPriceNumber ? formatPrice(originalPriceNumber) : ''} €/ud.
                </span>
              )}
            </div>

            {/* Add Button */}
            <button data-component-id="src/components/ui/ProductCard.tsx:391:12" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="391" data-component-file="ProductCard.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20w-11%20h-11%20top-%5B331px%5D%20left-%5B206px%5D%20bg-%5B%23fcc00a%5D%20rounded-%5B10px%5D%20p-0%20hover%3Abg-%5B%23fcc00a%5D%2F90%20flex%20items-center%20justify-center%20transition-all%20duration-300%20hover%3Ascale-110%20cursor-pointer%22%7D"
              onClick={handleAddToCart}
              className="absolute w-11 h-11 top-[331px] left-[206px] bg-[#fcc00a] rounded-[10px] p-0 hover:bg-[#fcc00a]/90 flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
            >
              <Plus data-component-id="src/components/ui/ProductCard.tsx:395:14" data-component-path="src/components/ui/ProductCard.tsx" data-component-line="395" data-component-file="ProductCard.tsx" data-component-name="Plus" data-component-content="%7B%22elementName%22%3A%22Plus%22%2C%22className%22%3A%22w-7%20h-7%20text-%5B%233d1c1a%5D%22%7D" className="w-7 h-7 text-[#3d1c1a]" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
